import React from 'react';
import PropTypes from 'prop-types';
import {
	IconMore,
} from '@wds/icons';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Checkbox } from '@wds/checkbox';
import { Menu } from '@wds/menu';
import { Pagination } from '@wds/pagination';
import { ProgressCircle } from '@wds/progress-circle';
import { Sheet } from '@wds/sheet';
import { Table } from '@wds/table';
import { Tooltip } from '@wds/tooltip';

import clip from 'text-clipper';

import _ from 'lodash';

import ajax from 'sma.common/ajax';
import { ApplicationPreviewModal } from 'sma.common/preview';
import { formatCurrency } from 'sma.common/financial_management/utils';
import { DownloadModal } from 'sma.common/downloads';
import modals from 'sma.common/components/modals';

import ReviewsTableHeader from './header';
import ReviewsTableToolbar from './toolbar';
import { STRINGS } from './constants';

export default class ReviewsTable extends React.Component {
	static proceedToReview(e, url, sortBy, sortDirection) {
		e.preventDefault();
		e.stopPropagation();

		if (sortBy && sortDirection) {
			const sortParam = sortBy.split('__').join('.');

			if (sortDirection == 'desc') {
				url += `?sort=-${sortParam}`;
			} else {
				url += `?sort=${sortParam}`;
			}
		}
		window.location.href = url;
	}

	// Disabling camelcase since we use the format to dynamically assign based on key
	/* eslint-disable camelcase */
	static format_collaborators__name(obj) {
		const names = [];
		for (const collaborator of obj.collaborators) {
			names.push(collaborator.name);
		}

		const result = names.join(', ');

		return clip(result, 250, { maxLines: 5 });
	}

	static format_collaborators__email(obj) {
		const emails = [];
		for (const collaborator of obj.collaborators) {
			emails.push(collaborator.email);
		}

		const result = emails.join(', ');

		return clip(result, 250, { maxLines: 5 });
	}

	static format_notes(obj) {
		return interpolate(gettext('%(total_count)s (%(new_count)s new)'), {
			total_count: obj.notes.total || 0,
			new_count: obj.notes.new || 0
		}, true);
	}

	static format_applicant(obj) {
		let template = obj.applicant;

		if (obj.logo) {
			template = (
				<div className="d-flex align-items-center">
					<img className="mr-1" src={obj.logo} width="36px" />
					<span>{clip(obj.applicant, 250, { maxLines: 5 })}</span>
				</div>
			);
		}

		return template;
	}

	static format_financial(obj, key) {
		const value = obj[key];

		return formatCurrency(value, false);
	}
	/* eslint-enable camelcase */

	debouncedSearch = _.debounce((query) => {
		this.setState({
			searchQuery: query,
			page: 0,
		}, () => {
			this.reload();
		});
	}, 2000);

	constructor(props) {
		super(props);

		this.state = {
			data: [],
			tableHeaders: [],
			selectedRows: [],
			sortDirection: 'asc',
			sortBy: null,
			isLoading: false,
			searchQuery: '',
			perPage: 10,
			page: 0,
			count: 0,
			filterIncomplete: 0,
			showReviewIframe: false,
			reviewIframeUrl: '',
			reviewIframeIsLoading: true,
			completeBeforeSubmit: props.completeBeforeSubmit,
		};

		this.fetchTableMetadata = this.fetchTableMetadata.bind(this);
		this.fetchTableData = this.fetchTableData.bind(this);

		this.handleRowClick = this.handleRowClick.bind(this);
		this.handleSelectAllClick = this.handleSelectAllClick.bind(this);

		this.viewApplication = this.viewApplication.bind(this);

		this.clearReview = this.clearReview.bind(this);
		this.clearSelectedReviews = this.clearSelectedReviews.bind(this);

		this.downloadApplications = this.downloadApplications.bind(this);
		this.downloadSelectedApplications = this.downloadSelectedApplications.bind(this);

		this.downloadReviews = this.downloadReviews.bind(this);
		this.downloadSelectedReviews = this.downloadSelectedReviews.bind(this);

		this.exportAllReviews = this.exportAllReviews.bind(this);
		this.exportSelectedReviews = this.exportSelectedReviews.bind(this);

		this.handleSortClick = this.handleSortClick.bind(this);
		this.handleSearch = this.handleSearch.bind(this);
		this.handleChangePage = this.handleChangePage.bind(this);
		this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
		this.handleFilterIncomplete = this.handleFilterIncomplete.bind(this);
	}

	componentDidMount() {
		this.loadPersistedState().then(() => (
			this.fetchTableMetadata()
		)).then((tableMetadata) => {
			const tableHeaders = tableMetadata.highlightColumns.map(item => ({
				label: item.label,
				key: item.key,
				financial: item.financial,
				sortable: item.sortable,
			}));

			const { sortBy } = this.state;
			let initialSort = sortBy;
			if (!initialSort) {
				initialSort = tableHeaders[0].key;
			}

			if (tableHeaders && tableHeaders.length > 0) {
				if (tableHeaders.length < 4) {
					document.getElementById('sma-js-assignment-list').className += ' small';
				}

				this.setState({
					tableHeaders,
					sortBy: initialSort,
				}, () => {
					this.reload();
				});
			}
		});

		// Bind events
		$('body').on('cancelClicked', () => { this.closeTaskRecordEdit(); });
		$('body').on('updateCompletedReviews', () => { this.reload(); });
	}

	getHeaderKeyForIndex(i) {
		const { tableHeaders } = this.state;
		return tableHeaders[i].key;
	}

	closeTaskRecordEdit = () => {
		this.setState({ showReviewIframe: false });
		this.reload();
	};

	handleChangePage = (event, page) => {
		this.setState({ page }, () => {
			this.persistState();
			this.reload();
		});
	};

	handleChangeRowsPerPage = (event, pages) => {
		this.setState({ isLoading: true });

		this.setState({ perPage: pages }, () => {
			this.persistState();
			this.reload();
		});
	};

	hideSpinner = () => {
		this.setState({ reviewIframeIsLoading: false });
	};

	handleSelectAllClick(e) {
		const { data } = this.state;
		if (e.target.checked) {
			this.setState({ selectedRows: data.map((row, i) => i) });
			return;
		}

		this.setState({ selectedRows: [] });
	}

	handleFilterIncomplete(event) {
		const { value } = event.target;

		this.setState({
			page: 0,
			filterIncomplete: value,
		}, () => {
			this.persistState();
			this.reload();
		});
	}

	handleRowClick(e, id) {
		// Prevent row clicking on other buttons within the row
		const buttons = e.target.closest('tr').querySelectorAll('button');

		for (const button of buttons) {
			if (button.contains(e.target)) {
				return;
			}
		}

		const { selectedRows } = this.state;
		const newSelection = selectedRows;

		if (selectedRows.includes(id)) {
			newSelection.splice(selectedRows.indexOf(id), 1);
		} else {
			newSelection.push(id);
		}

		this.setState({ selectedRows: newSelection });
	}

	handleSearch(e) {
		const { isLoading } = this.state;
		e.persist();
		const query = e.target.value;

		this.setState({
			searchQuery: query,
		});

		if (!isLoading) {
			this.setState({
				isLoading: true,
			});
		}
		this.debouncedSearch(query);
	}

	handleSortClick(e, header) {
		const {
			sortBy,
			sortDirection,
		} = this.state;

		let order = 'desc';

		if (sortBy === header.key && sortDirection === 'desc') {
			order = 'asc';
		}

		this.setState({
			sortBy: header.key,
			sortDirection: order,
		}, () => {
			this.persistState();
			this.reload();
		});
	}

	/**
	 * Makes a request to fetch table metadata
	 */
	fetchTableMetadata() {
		return new Promise((resolve, reject) => {
			const { awardId, roundId } = this.props;

			const url = reverse('reviewers-assignments-list-data', {
				award_id: awardId,
				round_id: roundId,
			});

			ajax.get({
				url,
				alertOnError: true,
				callback: (response) => {
					resolve(response);
				},
				onFail: (error) => {
					reject(error);
				},
			});
		});
	}

	persistState() {
		const {
			filterIncomplete,
			perPage,
			sortBy,
			sortDirection,
		} = this.state;
		const { awardId, roundId, userId } = this.props;

		const cacheKey = `sma.reviewer.table.${userId}.${awardId}.${roundId}`;
		localStorage.setItem(cacheKey, JSON.stringify({
			filterIncomplete,
			perPage,
			sortBy,
			sortDirection,
		}));
	}

	loadPersistedState() {
		return new Promise((resolve) => {
			// Load saved state from local storage
			const { awardId, roundId, userId } = this.props;

			const cacheKey = `sma.reviewer.table.${userId}.${awardId}.${roundId}`;
			const savedState = localStorage.getItem(cacheKey);

			if (savedState) {
				const parsedState = JSON.parse(savedState);
				const {
					filterIncomplete = 0,
					perPage = 10,
					sortBy = null,
					sortDirection = 'asc',
				} = parsedState;
				this.setState({
					filterIncomplete,
					perPage,
					sortBy,
					sortDirection,
				}, () => {
					resolve(true);
				});
			} else {
				resolve(false);
			}
		});
	}

	/**
	 * Makes an request to the api and returns a list of applications.
	 *
	 * @param {Object[]} tableHeaders
	 * @param {Object} config
	 */
	fetchTableData() {
		return new Promise((resolve, reject) => {
			const {
				completeBeforeSubmit,
				filterIncomplete,
				page,
				perPage,
				query,
				searchQuery,
				sortBy,
				sortDirection,
				tableHeaders,
			} = this.state;
			const { awardId, roundId, reviewerSubmitted } = this.props;

			let executedQuery = searchQuery;
			if (query && query !== undefined) {
				executedQuery = query;
			}
			if (tableHeaders.length < 1) {
				resolve([]);
			}

			// Request only the columns we display (reduces payload and load time)
			const requiredFields = ['id', 'title', 'current_task_record'];
			const columnKeys = tableHeaders.map(h => h.key);
			const fieldsSet = new Set([...requiredFields, ...columnKeys]);
			const fieldsParam = Array.from(fieldsSet).join(',');

			let url = `${reverse('api-smapply-applicationtable', {
				id: awardId,
			})}?round_id=${roundId}&fields=${encodeURIComponent(fieldsParam)}`;

			if (sortBy) {
				const sortParam = sortBy.split('__').join('.');

				if (sortDirection && sortDirection === 'desc') {
					url += `&sort=-${sortParam}`;
				} else {
					url += `&sort=${sortParam}`;
				}
			}

			if (executedQuery) {
				url += `&search=${executedQuery}`;
			}

			const updateContinueAndSaveButtons = (response) => {
				const continueButton = $('.sma-js-reviewer-alist-continue-reviewing');
				const submitBtn = $('body').find('.sma-js-submit-footer .sma-js-submit-reviews');
				if (continueButton.length) {
					const hasIncompleteReviews = response.has_incomplete_reviews;
					const submissionId = response.submission_id;
					if (hasIncompleteReviews && reviewerSubmitted === false) {
						let href = reverse('reviewers-review-mode', {
							award_id: awardId,
							round_id: roundId,
							submission_id: submissionId,
						});

						if (sortBy) {
							const sortParam = sortBy.split('__').join('.');

							if (sortDirection && sortDirection === 'desc') {
								href += `?sort=-${sortParam}`;
							} else {
								href += `?sort=${sortParam}`;
							}
						}

						continueButton.attr('href', href);
						continueButton.fadeIn(0);
						if (submitBtn.length && completeBeforeSubmit) {
							submitBtn.attr('disabled', 'disabled');
						}
					} else {
						continueButton.fadeOut(0);
						if (submitBtn.length) {
							submitBtn.removeAttr('disabled');
						}
					}
				}
			};

			const pageNum = parseInt(page)+1;

			url += `&per_page=${perPage}&page=${pageNum}&filterIncomplete=${filterIncomplete}`;
			ajax.get({
				url,
				alertOnError: true,
				headers: {
					Accept: 'application/vnd.smapply.table; charset=utf-8',
				},
				callback: (response) => {
					// Update last successful state
					this.setState({ searchQuery: executedQuery });

					const { results, count } = response;
					// Cache current page rows for Review Mode Summary (avoids redundant detail API call)
					const cacheKey = `sma.reviewer.tableRowCache.${awardId}.${roundId}`;
					const cache = {};
					results.forEach((row) => { cache[row.id] = row; });
					try {
						sessionStorage.setItem(cacheKey, JSON.stringify(cache));
					} catch (e) {
						// Ignore quota or serialization errors
					}
					resolve([results, count]);

					updateContinueAndSaveButtons(response.incomplete_reviews);
				},
				onFail: (response) => {
					reject(response);
				},
			});
		});
	}

	/**
	 * Filters down the table data to only include neccessary data and
	 * data specified by the admin (via application highlights)
	 *
	 * @param {*} tableData
	 * @param {*} tableHeaders
	 */
	cleanTableData(tableData, tableHeaders) {
		const { awardId, roundId } = this.props;

		function getReviewerName(response) {
			if (response !== undefined && response.reviewer !== undefined) {
				return response.reviewer;
			}

			return gettext('Reviewer');
		}

		function getRecommenderName(response) {
			if (response !== undefined && response.recommender !== undefined) {
				return response.recommender;
			}

			return gettext('Recommender');
		}

		let data = [];
		if (tableData) {
			data = tableData.map((item) => {
				const rowData = [];

				tableHeaders.forEach((tableHeader) => {
					const path = tableHeader.key.split('__');
					let obj = item;

					if (path[0] === 'recommender_responses') {
						if (_.hasIn(obj, path.join('.'))) {
							let recommenderResponses = _.get(obj, path.join('.'));
							recommenderResponses = recommenderResponses.map(response => (
								`${getRecommenderName(response)}: ${clip(response.response, 150, { maxLines: 3 })}`
							)).join('\n');
							_.set(obj, path.join('.'), recommenderResponses);
						}
					}

					if (path[0] === 'reviewer_responses') {
						if (_.hasIn(obj, path.join('.'))) {
							let reviewerResponses = _.get(obj, path.join('.'));
							reviewerResponses = reviewerResponses.map(response => (
								`${getReviewerName(response)}: ${clip(response.response, 150, { maxLines: 3 })}`
							)).join('\n');
							_.set(obj, path.join('.'), reviewerResponses);
						}
					}

					for (const currentPath of path) {
						if (obj.hasOwnProperty(currentPath)) {
							obj = obj[currentPath];
						} else {
							obj = '';
						}
					}

					if (`format_${tableHeader.key}` in ReviewsTable) {
						obj = ReviewsTable[`format_${tableHeader.key}`](item);
					} else if (tableHeader.financial) {
						obj = ReviewsTable.format_financial(item, tableHeader.key);
					} else {
						obj = clip(obj, 250, { maxLines: 5 });
					}

					rowData.push({
						id: `${item.id}-${tableHeader.key}`,
						value: obj,
					});
				});

				const reviewModeURL = reverse('reviewers-review-mode', {
					award_id: awardId,
					round_id: roundId,
					submission_id: item.id,
				});

				const editURL = reverse('reviewers-task-quick-edit', {
					award_id: awardId,
					round_id: roundId,
					submission_id: item.id,
				});

				return {
					cells: rowData,
					id: item.id,
					title: item.title,
					reviewURL: reviewModeURL,
					editURL,
					taskRecordId: item.current_task_record.id,
					started: item.current_task_record.started,
					finished: item.current_task_record.finished,
				};
			});
		}

		return data;
	}

	editReview(e, index) {
		const { data } = this.state;
		const url = data[index].editURL;

		this.setState({
			showReviewIframe: true,
			reviewIframeUrl: url,
			reviewIframeIsLoading: true,
		});
	}

	clearSelectedReviews() {
		const { selectedRows } = this.state;
		this.clearReview(selectedRows);
	}

	clearReview(rowIndices) {
		const { roundId, awardId } = this.props;
		const { data } = this.state;

		const ids = rowIndices.map(index => data[index].id);

		/* eslint-disable no-new */
		new modals.Confirm(
			gettext('Reset selected reviews'),
			gettext('Resetting reviews will remove all review data for the selected applications. This cannot be undone.'),
			() => {
				ajax.post({
					url: reverse('reviewers-reset-task', {
						award_id: awardId,
						round_id: roundId,
					}),
					data: {
						submissions: ids,
					},
					callback: () => {
						this.reload();
					},
				});
			},
		);
		/* eslint-enable no-new */
	}

	reportConflict(e, index) {
		const {
			data,
		} = this.state;

		const item = data[index];

		const modal = new modals.AjaxFormModal({
			destroyOnClose: true,
			autoShow: true,
			size: 'lg',
			useButtonLoaders: true,
		});

		const modalContent = new modals.AjaxFormContent(
			reverse('reviewers-report-conflict', {
				submission_id: item.id,
			}),
			{
				title: gettext('Conflict of interest'),
				buttons: [{
					label: gettext('CANCEL'),
					cls: 'btn btn-link btn-loud',
					handler: () => modal.hide(),
				}, {
					label: gettext('OK'),
					cls: 'btn btn-primary btn-loud',
					handler: (contentModal) => {
						const form = contentModal.modalBody.find('form')[0];
						const formData = new FormData(form);
						ajax.post({
							url: reverse('reviewers-report-conflict', { submission_id: item.id }),
							data: formData,
							contentType: false,
							processData: false,
							callback: () => {
								contentModal.hide();
								this.reload();
							},
						});
					},
				}],
			},
		);

		modal.addToStack(
			'ConflictOfInterest',
			modalContent,
		);
	}

	reload() {
		const {
			tableHeaders,
		} = this.state;

		this.setState({
			isLoading: true,
		}, () => {
			this.fetchTableData().then(([rawTableData, count]) => {
				const cleanedTableData = this.cleanTableData(rawTableData, tableHeaders);

				this.setState({
					data: cleanedTableData,
					selectedRows: [],
					isLoading: false,
					count,
				});
			}).catch(() => {
				this.setState({
					isLoading: false,
				});
			});
		});
	}

	viewApplication(e, index) {
		const { roundId, applicantStages } = this.props;
		const { data } = this.state;
		const item = data[index];

		const applicationPreviewModal = new ApplicationPreviewModal({
			destroyOnClose: true,
			title: item.title,
			size: 'lg',
			buttons: [],
			previewUrl: 'submission-stage-preview',
			showPreview: true,
			application: item.id,
			fromRound: roundId,
			stageIds: applicantStages.map(round => round.id),
		});

		applicationPreviewModal.show();
	}

	downloadSelectedApplications() {
		const { selectedRows } = this.state;
		this.downloadApplications(selectedRows);
	}

	downloadApplications(rowIndices) {
		const { roundId } = this.props;
		const { data } = this.state;

		const ids = rowIndices.map(index => data[index].id);

		/* eslint-disable no-new */
		new DownloadModal({
			title: gettext('Download options'),
			submission_ids: ids,
			from_round_id: roundId,
		});
		/* eslint-enable no-new */
	}

	downloadSelectedReviews() {
		const { selectedRows } = this.state;
		this.downloadReviews(selectedRows);
	}

	downloadReviews(rowIndices) {
		const { roundId } = this.props;
		const { data } = this.state;

		const taskRecordIds = [];
		const ids = rowIndices.map((index) => {
			const item = data[index];
			taskRecordIds.push(item.taskRecordId);
			return item.id;
		});

		ajax.post({
			url: reverse('download-redirect'),
			data: {
				submission_ids: ids,
				stage_ids: [roundId],
				from_round_id: roundId,
				task_record_ids: taskRecordIds,
			},
			alertOnSuccess: false,
			callback: (response) => {
				if (response.url) {
					window.open(response.url, '_blank');
				}
			},
		});
	}

	exportSelectedReviews() {
		const { selectedRows } = this.state;
		this.exportAllReviews(selectedRows);
	}

	exportAllReviews(rowIndices) {
		const { roundId, awardId } = this.props;
		const { data } = this.state;

		const ids = rowIndices.map(index => data[index].id);

		ajax.post({
			url: reverse('download-redirect'),
			data: {
				submission_ids: ids,
				stage_ids: [roundId],
				from_round_id: roundId,
				redirect: {
					reverse_url: 'reviewers-download-all-reviews',
					params: [awardId, roundId],
				},
			},
			alertOnSuccess: false,
			callback: (response) => {
				if (response.url) {
					window.open(response.url, '_blank');
				}
			},
		});
	}

	render() {
		const {
			data,
			filterIncomplete,
			tableHeaders,
			isLoading,
			selectedRows,
			sortBy,
			sortDirection,
			searchQuery,
			count,
			perPage,
			page,
			reviewIframeIsLoading,
			reviewIframeUrl,
			showReviewIframe,
		} = this.state;

		const {
			allowConflicts,
			canEdit,
			reviewerSubmitted,
		} = this.props;

		return (
			<React.Fragment>
				<ReviewsTableToolbar
					numSelected={selectedRows.length}
					downloadSelectedApplications={this.downloadSelectedApplications}
					downloadSelectedReviews={this.downloadSelectedReviews}
					clearSelectedReviews={this.clearSelectedReviews}
					exportSelectedReviews={this.exportSelectedReviews}
					handleSearch={this.handleSearch}
					searchQuery={searchQuery}
					filterIncomplete={parseInt(filterIncomplete, 10)}
					handleFilterIncomplete={this.handleFilterIncomplete}
					canEdit={canEdit}
				/>
				<div className={`sma-review-table-body ${(isLoading ? 'sma-reviewer-table-spinner-mask' : '')}`}>
					<Table>
						<ReviewsTableHeader
							columnHeaders={tableHeaders}
							handleSelectAllClick={this.handleSelectAllClick}
							selectedRows={selectedRows}
							rowCount={data.length}
							sortBy={sortBy}
							sortDirection={sortDirection}
							handleSortClick={this.handleSortClick}
							canEdit={canEdit}
							allowConflicts={allowConflicts}
						/>
						<Table.Body>
							{data.map((row, i) => {
								const isSelected = selectedRows.includes(i);

								const RowProgressIcon = () => {
									const tmpl = (cls, icon = '') => (
										<div className="sma-task-completion sma-automation-task-completion">
											<div className={cls}>{icon}</div>
										</div>
									);

									let cls = '';

									if (reviewerSubmitted) {
										cls += 'disabled';
									}

									if (row.finished) {
										cls += ' complete';
										return tmpl(cls, <i className="nc-icon-mini ui-1_check-simple" />);
									}

									if (!row.started) {
										cls += ' notstarted';
										return tmpl(cls);
									}

									cls += ' inprogress';

									return tmpl(cls);
								};

								const RowButton = (props) => {
									const { sortBy, sortDirection } = props;
									const tmpl = (msg, classes) => (
										<button
											type="button"
											className={classes}
											onClick={e => ReviewsTable.proceedToReview(e, row.reviewURL, sortBy, sortDirection)}
										>
											<strong>{msg}</strong>
										</button>
									);

									if (row.finished || reviewerSubmitted) {
										return tmpl(
											gettext('View'),
											'sma-automation-application-view btn-hollow-default btn btn-loud w-100',
										);
									}

									if (!row.started) {
										return tmpl(
											gettext('Start'),
											'sma-automation-application-start btn-hollow-primary btn btn-loud w-100',
										);
									}

									return tmpl(
										gettext('Continue'),
										'sma-automation-application-continue btn-hollow-primary btn btn-loud w-100',
									);
								};

								const CellValue = (props) => {
									const { row: cellRow, cell, index, sortBy, sortDirection } = props;
									const key = this.getHeaderKeyForIndex(index);

									if (index > 0) {
										return (
											<Table.Cell
												data-testid={`sma-automation-table-column-${key}`}
												key={key}
											>
												{ cell.value }
											</Table.Cell>
										);
									}

									return (
										<Table.Cell
											key={key}
											data-testid={`sma-automation-table-column-${key}`}
										>
											<a
												href="#"
												className="sma-reviewer-table-first-column-link"
												onClick={e => ReviewsTable.proceedToReview(e, cellRow.reviewURL, sortBy, sortDirection)}
											>
												{cell.value}
											</a>
										</Table.Cell>
									);
								};

								return (
									<Table.Row
										key={row.id}
										onClick={e => this.handleRowClick(e, i)}
									>
										<React.Fragment>
											<Table.Cell
												key="checkbox-column"
												data-testid="sma-automation-table-column-checkbox"
											>
												<Checkbox checked={isSelected} />
											</Table.Cell>

											<Table.Cell key="progress-column">
												<RowProgressIcon />
											</Table.Cell>

											{row.cells.map((cell, index) => (
												<CellValue
													key={cell.id}
													row={row}
													cell={cell}
													index={index}
													sortBy={sortBy}
													sortDirection={sortDirection}
												/>
											))}

											{ allowConflicts && (
												<Table.Cell
													data-testid="sma-automation-conflict"
													key="conflict-column"
												>
													<Tooltip
														text={gettext(
															'Mark this applicant with a conflict of interest',
														)}
													>
														<Button
															color="secondary"
															variant="text"
															onClick={e => this.reportConflict(e, i)}
														>
															<i className="nc-icon-mini location_flag-points-32" />
														</Button>
													</Tooltip>
												</Table.Cell>
											)}

											{ canEdit && (
												<Table.Cell
													data-testid="sma-automation-edit"
													key="edit-column"
												>
													<Tooltip
														text={gettext('Quick review')}
													>
														<Button
															data-testid="sma-automation-edit-button"
															color="secondary"
															variant="text"
															onClick={e => this.editReview(e, i)}
														>
															<i className="nc-icon-mini ui-1_pencil" />
														</Button>
													</Tooltip>
												</Table.Cell>
											)}

											<Table.Cell
												data-testid="sma-automation-table-column-button"
												key="button-column"
											>
												<RowButton
													sortBy={sortBy}
													sortDirection={sortDirection}
												/>
											</Table.Cell>

											<Table.Cell
												key="action-column"
												data-testid="sma-automation-more-actions"
											>
												<Menu
													placement="bottom-right"
													dense
													menuTrigger={
														(
															<Button
																data-testid="sma-automation-more-actions-button"
																color="secondary"
																variant="text"
																label="more action button"
															>
																<IconMore />
															</Button>
														)
													}
												>
													<React.Fragment>
														{ canEdit && (
															<Menu.Item
																data-testid="sma-automation-clear-review-action"
																key="clear-review-action"
																onClick={() => this.clearReview([i])}
															>
																{ gettext('Clear review') }
															</Menu.Item>
														)}
													</React.Fragment>
													<Menu.Item
														data-testid="sma-automation-view-application-action"
														key="view-application-action"
														onClick={e => this.viewApplication(e, i)}
													>
														{ gettext('View application') }
													</Menu.Item>
													<Menu.Item
														data-testid="sma-automation-download-application-action"
														key="download-application-action"
														onClick={() => this.downloadApplications([i])}
													>
														{ gettext('Download application') }
													</Menu.Item>
													<Menu.Item
														data-testid="sma-automation-download-review-action"
														key="download-review-action"
														onClick={() => this.downloadReviews([i])}
													>
														{ gettext('Download my review') }
													</Menu.Item>
													<Menu.Item
														data-testid="sma-automation-export-all-action"
														key="export-all-action"
														onClick={() => this.exportAllReviews([i])}
													>
														{ gettext('Export all reviews (PDF)') }
													</Menu.Item>
												</Menu>
											</Table.Cell>
										</React.Fragment>
									</Table.Row>
								);
							})}
						</Table.Body>
					</Table>
					<Box my={4}>
						<Pagination
							labelJumpToPage={STRINGS.labelJumpToPage}
							labelLegend={STRINGS.labelLegend}
							labelRowsPerPage={STRINGS.labelRowsPerPage}
							hasPageSelect
							hasRowsPerPage
							rowsPerPage={perPage}
							page={page}
							count={count}
							onChangePage={this.handleChangePage}
							onChangeRowsPerPage={this.handleChangeRowsPerPage}
						/>
					</Box>
					<Sheet
						show={showReviewIframe}
						onClose={this.closeTaskRecordEdit}
						placement="right"
						size="lg"
					>
						{
							reviewIframeIsLoading && (
								<div className="wds-h-100 wds-flex wds-flex--x-center wds-flex--y-center">
									<ProgressCircle continuous size="lg" />
								</div>
							)
						}
						<div
							className={'sma-application-taskrecord-edit-pane '
								+ `${(reviewIframeIsLoading ? 'wds-display--none' : '')}`}
						>
							<a
								onClick={this.closeTaskRecordEdit}
								className="sma-taskrecord-edit-close"
								href="#"
							>
								<i className="nc-icon-mini ui-1_simple-remove" />
							</a>
							<iframe
								title="Quick Review"
								src={reviewIframeUrl}
								onLoad={this.hideSpinner}
							/>
						</div>
					</Sheet>
				</div>
				{
					isLoading && (
						<span
							className="sma-reviewer-table-spinner"
						>
							<ProgressCircle continuous />
						</span>
					)
				}
			</React.Fragment>
		);
	}
}

ReviewsTable.propTypes = {
	allowConflicts: PropTypes.bool.isRequired,
	applicantStages: PropTypes.array,
	awardId: PropTypes.number.isRequired,
	canEdit: PropTypes.bool.isRequired,
	completeBeforeSubmit: PropTypes.bool.isRequired,
	data: PropTypes.array,
	reviewerSubmitted: PropTypes.bool.isRequired,
	roundId: PropTypes.number.isRequired,
	userId: PropTypes.number.isRequired,
};

ReviewsTable.defaultProps = {
	data: [],
};
